import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Button, Card, CardContent, Typography, Chip, Grid,
  TextField, Select, MenuItem, FormControl, CircularProgress,
  IconButton, Tooltip, InputAdornment,
  Stack, Avatar, Divider, Dialog, DialogTitle, DialogContent, DialogActions,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import TuneIcon from '@mui/icons-material/Tune';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import DownloadIcon from '@mui/icons-material/Download';
import { downloadCsv } from '../../utils/exportCsv';
import { projectAPI, statusAPI } from '../../api';
import { useToast } from '../../components/common/Toast';
import { useAuth } from '../../context/AuthContext';

const ROLE_COLORS = {
  admin: '#FF6B35', support_team: '#4472C4', team_member: '#2E7D32',
  qa_team: '#7B1FA2', higher_authority: '#C62828',
};
const TASK_STATUS_COLORS = {
  'In Progress': { bg: '#1565C018', color: '#1565C0' },
  'Completed':   { bg: '#2E7D3218', color: '#2E7D32' },
  'Blocked':     { bg: '#C6282818', color: '#C62828' },
  'On Hold':     { bg: '#E6510018', color: '#E65100' },
  'Review':      { bg: '#7B1FA218', color: '#7B1FA2' },
};

const STATUS = {
  planning:  { color: '#4472C4', label: 'Planning' },
  active:    { color: '#2E7D32', label: 'Active' },
  on_hold:   { color: '#E65100', label: 'On Hold' },
  completed: { color: '#0277BD', label: 'Completed' },
  cancelled: { color: '#C62828', label: 'Cancelled' },
};

const fmtDate = (d) => {
  if (!d) return '—';
  const p = d.split('-');
  const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${parseInt(p[2])} ${m[parseInt(p[1])-1]} ${p[0]}`;
};

export default function ProjectsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isSupportTeam, isAdmin } = useAuth();
  const { showToast } = useToast();
  const canCreate = isSupportTeam() || isAdmin();
  const canUpdateProject = isSupportTeam() || isAdmin() || ['qa_team', 'team_member'].includes(user?.role);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    status: searchParams.get('status') || '',
  });
  const [statusDialogProject, setStatusDialogProject] = useState(null);
  const [statusList, setStatusList] = useState([]);
  const [statusLoading, setStatusLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grid');

  const openStatusDialog = (e, project) => {
    e.stopPropagation();
    setStatusDialogProject(project);
    setStatusLoading(true);
    statusAPI.getAll(project.id)
      .then(res => setStatusList(res.data || []))
      .catch(() => showToast('Failed to load daily status', 'error'))
      .finally(() => setStatusLoading(false));
  };

  const closeStatusDialog = () => {
    setStatusDialogProject(null);
    setStatusList([]);
  };

  const load = useCallback(() => {
    setLoading(true);
    // The backend paginates (default 10/page), but this page renders every
    // matching project as cards/rows with client-side status chips & filters
    // rather than server-side pagination — so request a high limit to fetch
    // the full result set instead of just page 1.
    const apiFilters = { ...filters, limit: 1000 };
    if (apiFilters.status === 'overdue' || apiFilters.status === 'open') {
      delete apiFilters.status; // not a real DB status — filtered client-side below
    }
    projectAPI.getAll(apiFilters)
      .then(res => setProjects(res.data.projects || []))
      .catch(() => showToast('Failed to load projects', 'error'))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const isOverdue = (p) => {
    if (!p.end_date || ['completed', 'cancelled'].includes(p.status)) return false;
    return new Date(p.end_date) < new Date();
  };

  const displayedProjects = filters.status === 'overdue'
    ? projects.filter(isOverdue)
    : filters.status === 'open'
      ? projects.filter(p => !['completed', 'cancelled'].includes(p.status))
      : projects;

  const handleDownload = () => {
    if (!displayedProjects.length) return showToast('No projects to download', 'error');
    downloadCsv(
      `projects-${new Date().toISOString().slice(0, 10)}.csv`,
      ['#', 'Project', 'Status', 'Category', 'Start Date', 'End Date', 'TAT Date', 'Latest Remark', 'Remark By', 'Description'],
      displayedProjects.map((p, i) => [
        i + 1, p.name, STATUS[p.status]?.label || p.status,
        p.category || '', p.start_date || '', p.end_date || '', p.tat_date || '',
        p.latest_remark?.remark || '', p.latest_remark?.author || '', p.description || '',
      ])
    );
  };

  const statusCounts = Object.keys(STATUS).reduce((acc, s) => {
    acc[s] = projects.filter(p => p.status === s).length;
    return acc;
  }, {});

  const emptyStateContent = (
    <>
      <FolderOpenIcon sx={{ fontSize: 56, opacity: 0.12, display: 'block', mx: 'auto', mb: 1.5 }} />
      <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 700 }}>No projects found</Typography>
      <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
        {filters.search || filters.status ? 'Try adjusting your filters' : 'Create your first project to get started'}
      </Typography>
      {canCreate && !filters.search && !filters.status && (
        <Button variant="contained" startIcon={<AddIcon />} sx={{ mt: 2.5, borderRadius: 2, fontWeight: 700 }}
          onClick={() => navigate('/projects/new')}>
          Create First Project
        </Button>
      )}
    </>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: '-0.5px' }}>Projects</Typography>
          <Typography color="text.secondary" variant="body2" sx={{ mt: 0.3 }}>
            {displayedProjects.length} project{displayedProjects.length !== 1 ? 's' : ''} found
          </Typography>
        </Box>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => navigate('/projects/new')}
            sx={{ borderRadius: 2, fontWeight: 700, px: 2.5, boxShadow: '0 4px 14px rgba(30,58,95,0.25)' }}>
            New Project
          </Button>
        )}
      </Box>

      {/* Status summary chips */}
      {projects.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
          {Object.entries(STATUS).map(([key, s]) => statusCounts[key] > 0 && (
            <Box key={key} onClick={() => setFilters(f => ({ ...f, status: f.status === key ? '' : key }))}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.8, px: 1.5, py: 0.6, borderRadius: 5,
                cursor: 'pointer', border: '1px solid',
                borderColor: filters.status === key ? s.color : s.color + '30',
                bgcolor: filters.status === key ? s.color + '15' : 'transparent',
                transition: 'all 0.15s',
                '&:hover': { borderColor: s.color, bgcolor: s.color + '10' } }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: s.color }} />
              <Typography variant="caption" sx={{ fontWeight: 700, color: s.color, fontSize: '0.72rem' }}>
                {s.label}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 800, color: s.color, fontSize: '0.72rem' }}>
                {statusCounts[key]}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3, boxShadow: 'none', border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <CardContent sx={{ pb: '16px !important', pt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <TuneIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
            <TextField size="small" placeholder="Search projects..."
              value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })}
              sx={{ flex: 1, minWidth: 200 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }} />
            <Select size="small" value={filters.status} displayEmpty sx={{ minWidth: { xs: '100%', sm: 130 }, borderRadius: 2 }}
              onChange={e => setFilters({ ...filters, status: e.target.value })}>
              <MenuItem value="">All Status</MenuItem>
              {Object.entries(STATUS).map(([k, s]) => <MenuItem key={k} value={k}>{s.label}</MenuItem>)}
              <MenuItem value="open">Open (not completed)</MenuItem>
              <MenuItem value="overdue">Overdue</MenuItem>
            </Select>
            {(filters.search || filters.status) && (
              <Button size="small" onClick={() => setFilters({ search: '', status: '' })}
                sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>Clear</Button>
            )}
            <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload}
              sx={{ ml: { xs: 0, sm: 'auto' }, borderRadius: 2, fontWeight: 700 }}>
              Download
            </Button>
            <ToggleButtonGroup size="small" exclusive value={viewMode}
              onChange={(e, val) => val && setViewMode(val)}>
              <ToggleButton value="grid" aria-label="Grid view" sx={{ px: 1.2, py: 0.5 }}>
                <GridViewIcon sx={{ fontSize: 18 }} />
              </ToggleButton>
              <ToggleButton value="list" aria-label="List view" sx={{ px: 1.2, py: 0.5 }}>
                <ViewListIcon sx={{ fontSize: 18 }} />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </CardContent>
      </Card>


      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>
      ) : viewMode === 'grid' ? (
        <Grid container spacing={2.5}>
          {displayedProjects.map((p) => {
            const st = STATUS[p.status] || STATUS.planning;
            return (
              <Grid item xs={12} sm={6} lg={4} xl={3} key={p.id}>
                <Card onClick={() => navigate(`/projects/${p.id}`)}
                  sx={{ height: '100%', cursor: 'pointer', borderRadius: 3, border: '1px solid', borderColor: 'divider',
                    boxShadow: 'none', transition: 'all 0.18s',
                    '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 12px 32px ${st.color}18`, borderColor: st.color + '50' } }}>

                  {/* Top color bar */}
                  <Box sx={{ height: 3, bgcolor: st.color, borderRadius: '12px 12px 0 0' }} />

                  <CardContent sx={{ p: 2.5 }}>
                    {/* Row 1: avatar + priority + edit */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Avatar sx={{ width: 44, height: 44, bgcolor: st.color + '18', color: st.color, fontWeight: 900, fontSize: '1.1rem', border: `2px solid ${st.color}25` }}>
                        {p.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Tooltip title="View project">
                          <IconButton size="small" onClick={e => { e.stopPropagation(); navigate(`/projects/${p.id}`); }}
                            sx={{ width: 28, height: 28, bgcolor: 'action.hover' }}>
                            <VisibilityIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                        {canUpdateProject && (
                          <Tooltip title="Edit project">
                            <IconButton size="small" onClick={e => { e.stopPropagation(); navigate(`/projects/${p.id}/edit`); }}
                              sx={{ width: 28, height: 28, bgcolor: 'action.hover' }}>
                              <EditIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="View status">
                          <IconButton size="small" onClick={e => openStatusDialog(e, p)}
                            sx={{ width: 28, height: 28, bgcolor: 'action.hover' }}>
                            <EventNoteIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {/* Name + desc */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5, letterSpacing: '-0.2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary"
                      sx={{ mb: 2.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', minHeight: 36, fontSize: '0.8rem', lineHeight: 1.5 }}>
                      {p.description || 'No description provided.'}
                    </Typography>

                    {/* Current Status */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Current Status</Typography>
                      <Chip label={st.label} size="small"
                        sx={{ bgcolor: st.color + '15', color: st.color, fontWeight: 700, fontSize: '0.72rem', height: 24, border: `1px solid ${st.color}30` }} />
                    </Box>

                    <Divider sx={{ mb: 2, borderStyle: 'dashed' }} />

                    {/* Footer */}
                    {p.latest_remark && (
                      <Tooltip title={`${p.latest_remark.author || ''}: ${p.latest_remark.remark}`}>
                        <Box sx={{ mb: 1.5, p: 1, borderRadius: 1.5, bgcolor: 'action.hover' }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.68rem' }}>
                            Remark{p.remark_count > 1 ? ` (${p.remark_count})` : ''} · {p.latest_remark.author}
                          </Typography>
                          <Typography variant="caption" color="text.secondary"
                            sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontSize: '0.74rem', lineHeight: 1.4 }}>
                            {p.latest_remark.remark}
                          </Typography>
                        </Box>
                      </Tooltip>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', fontWeight: 700 }}>TAT</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>{fmtDate(p.tat_date)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarTodayIcon sx={{ fontSize: 11, color: 'text.disabled' }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                          {fmtDate(p.end_date)}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}

          {displayedProjects.length === 0 && (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', py: 10, bgcolor: 'background.paper', borderRadius: 3, border: '1px dashed', borderColor: 'divider' }}>
                {emptyStateContent}
              </Box>
            </Grid>
          )}
        </Grid>
      ) : (
        <Stack spacing={1.5}>
          {displayedProjects.map((p) => {
            const st = STATUS[p.status] || STATUS.planning;
            return (
              <Card key={p.id} onClick={() => navigate(`/projects/${p.id}`)}
                sx={{ cursor: 'pointer', borderRadius: 2.5, border: '1px solid', borderColor: 'divider',
                  boxShadow: 'none', transition: 'all 0.15s',
                  '&:hover': { borderColor: st.color + '50', boxShadow: `0 6px 18px ${st.color}12` } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                  <Avatar sx={{ width: 40, height: 40, bgcolor: st.color + '18', color: st.color, fontWeight: 900, flexShrink: 0 }}>
                    {p.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary"
                      sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.description || 'No description provided.'}
                    </Typography>
                    {p.latest_remark && (
                      <Typography variant="caption" color="text.secondary"
                        sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontStyle: 'italic' }}>
                        Remark ({p.latest_remark.author}): {p.latest_remark.remark}
                      </Typography>
                    )}
                  </Box>
                  <Chip label={st.label} size="small"
                    sx={{ bgcolor: st.color + '15', color: st.color, fontWeight: 700, fontSize: '0.68rem', height: 22, border: `1px solid ${st.color}25` }} />
                  <Box sx={{ display: { xs: 'none', md: 'block' }, minWidth: 90 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', fontWeight: 700 }}>TAT: {fmtDate(p.tat_date)}</Typography>
                  </Box>
                  <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5, minWidth: 90 }}>
                    <CalendarTodayIcon sx={{ fontSize: 11, color: 'text.disabled' }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                      {fmtDate(p.end_date)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexShrink: 0 }}>
                    <Tooltip title="View project">
                      <IconButton size="small" onClick={e => { e.stopPropagation(); navigate(`/projects/${p.id}`); }}
                        sx={{ width: 28, height: 28, bgcolor: 'action.hover' }}>
                        <VisibilityIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    {canUpdateProject && (
                      <Tooltip title="Edit project">
                        <IconButton size="small" onClick={e => { e.stopPropagation(); navigate(`/projects/${p.id}/edit`); }}
                          sx={{ width: 28, height: 28, bgcolor: 'action.hover' }}>
                          <EditIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="View status">
                      <IconButton size="small" onClick={e => openStatusDialog(e, p)}
                        sx={{ width: 28, height: 28, bgcolor: 'action.hover' }}>
                        <EventNoteIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Card>
            );
          })}

          {displayedProjects.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 10, bgcolor: 'background.paper', borderRadius: 3, border: '1px dashed', borderColor: 'divider' }}>
              {emptyStateContent}
            </Box>
          )}
        </Stack>
      )}

      {/* Daily Status Dialog */}
      <Dialog open={!!statusDialogProject} onClose={closeStatusDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontWeight: 800 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EventNoteIcon sx={{ color: '#1E3A5F' }} />
            Daily Status{statusDialogProject ? ` — ${statusDialogProject.name}` : ''}
          </Box>
          <IconButton size="small" onClick={closeStatusDialog}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {statusLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : statusList.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              No status updates yet for this project.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {statusList.map((s) => {
                const taskStatus = s.tasks_planned || 'In Progress';
                const style = TASK_STATUS_COLORS[taskStatus] || TASK_STATUS_COLORS['In Progress'];
                return (
                  <Box key={s.id} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
                      <Avatar
                        sx={{
                          width: 30, height: 30, fontSize: '0.75rem', fontWeight: 700,
                          bgcolor: (ROLE_COLORS[s.author?.role] || '#888') + '30',
                          color: ROLE_COLORS[s.author?.role] || '#888',
                        }}
                      >
                        {s.author?.name?.charAt(0)}
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                          {s.author?.name || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {s.date}{s.start_time && s.end_time ? ` · ${s.start_time} - ${s.end_time}` : s.hours_worked ? ` · ${s.hours_worked}h` : ''}
                        </Typography>
                      </Box>
                      <Chip label={taskStatus} size="small" sx={{ bgcolor: style.bg, color: style.color, fontWeight: 700 }} />
                      <Chip
                        icon={<CheckCircleOutlineIcon sx={{ fontSize: '13px !important' }} />}
                        label={`${s.completion_percentage || 0}%`}
                        size="small" variant="outlined" sx={{ fontWeight: 700 }}
                      />
                    </Box>
                    {s.tasks_completed && (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {s.tasks_completed}
                      </Typography>
                    )}
                    {s.blockers && s.blockers !== 'None' && (
                      <Typography variant="body2" sx={{ mt: 0.5, color: 'error.main', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        ⚠️ {s.blockers}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeStatusDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}